import { Injectable, computed, signal, inject } from '@angular/core';
import Keycloak from 'keycloak-js';

/**
 * Tenant context service for multi-tenancy.
 * Extracts tenant_id from JWT claims and provides it to interceptors.
 * 
 * Note: keycloak-angular's KeycloakService doesn't expose claim extraction,
 * so we use the Keycloak instance directly for tokenParsed access.
 */
@Injectable({ providedIn: 'root' })
export class TenantService {
    // Inject the Keycloak instance directly (provided by keycloak-angular)
    private readonly keycloak = inject(Keycloak, { optional: true });

    private readonly _tenantId = signal<string | null>(null);

    /** Set by {@link clear} so a logged-out session does not immediately re-derive
     *  the tenant from a token Keycloak has not torn down yet. Reset by {@link init}. */
    private cleared = false;

    /** Current tenant ID (reactive) */
    readonly tenantId = computed(() => this._tenantId());

    /**
     * Synchronous getter for interceptors and the tenant-keyed caches.
     *
     * Resolves on demand rather than relying on someone having called
     * {@link init}: this service is `providedIn: 'root'` and can be constructed
     * before Keycloak finishes authenticating, and nothing revisits it afterwards.
     * Left unresolved the tenant stays null for the whole session, which silently
     * disables every tenant-keyed cache — the POS catalog's IndexedDB copy is
     * neither read nor written without a tenant, so the till refetched the entire
     * catalog on every page load.
     */
    id(): string | null {
        if (this._tenantId() === null && !this.cleared) {
            this.extractTenantFromToken();
        }
        return this._tenantId();
    }

    /** Check if tenant is resolved */
    readonly isResolved = computed(() => this._tenantId() !== null);

    /**
     * Initialize tenant from JWT claims.
     * Call this after successful Keycloak login, or rely on lazy extraction.
     */
    init(): void {
        this.cleared = false;
        this.extractTenantFromToken();
    }

    /** 
     * Extract tenant from already-parsed token (no manual decode needed).
     * keycloak-js exposes tokenParsed which contains the decoded JWT payload.
     */
    private extractTenantFromToken(): void {
        try {
            if (!this.keycloak?.authenticated) {
                this._tenantId.set(null);
                return;
            }

            // Use tokenParsed from keycloak-js (already decoded JWT payload)
            const claims = this.keycloak.tokenParsed as Record<string, unknown> | undefined;
            const tenantId = (claims?.['tenant_id'] ?? claims?.['tenantId'] ?? null) as string | null;
            this._tenantId.set(tenantId);
        } catch (e) {
            console.error('[TenantService] Failed to extract tenant_id from token', e);
            this._tenantId.set(null);
        }
    }

    /** Clear tenant on logout. Sticky until {@link init} — otherwise the next
     *  `id()` would re-derive it from a token that has not been dropped yet. */
    clear(): void {
        this.cleared = true;
        this._tenantId.set(null);
    }
}
